<template>
  <div class="info-section">
    <div class="section-header">
      <h4 class="gm-label">Connections</h4>
      <EditInDjangoAdmin
          :id="room.id"
          app="world"
          model="position"
      />
    </div>
    <div class="connections-container">
      <div v-if="roomConnections.length > 0" class="connection-list">
        <div
            v-for="link in resolvedConnections"
            :key="link.connection.id"
            class="connection-item"
            @click="openConnectionInfo(link.connection)"
        >
          <!-- One glyph carries the whole answer to "which way": a compass
               arrow rotated to the bearing, or a ladder for a change of floor. -->
          <span class="connection-dial" :class="{ 'is-vertical': link.isVertical }" :title="link.label">
            <!-- An arrow carrying ladder rungs: which way, and that it is a
                 change of floor, in one shape that survives 16px. -->
            <svg v-if="link.isVertical" class="dial-glyph" viewBox="0 0 20 20" aria-hidden="true">
              <template v-if="link.up">
                <path d="M10 17V4.2M10 4.2L6.6 7.6M10 4.2l3.4 3.4"/>
                <path class="dial-rungs" d="M7.6 11.6h4.8M7.6 14.4h4.8"/>
              </template>
              <template v-else>
                <path d="M10 3v12.8M10 15.8l-3.4-3.4M10 15.8l3.4-3.4"/>
                <path class="dial-rungs" d="M7.6 5.6h4.8M7.6 8.4h4.8"/>
              </template>
            </svg>
            <svg v-else class="dial-glyph" viewBox="0 0 20 20" :style="{ transform: `rotate(${link.bearing}deg)` }" aria-hidden="true">
              <path d="M10 3.2v13.6M10 3.2l-3.6 3.6M10 3.2l3.6 3.6"/>
            </svg>
          </span>

          <div class="connection-info">
            <div class="connection-header">
              <span class="connection-direction">{{ link.label }}</span>
              <span class="connection-target">{{ link.targetName }}</span>
            </div>
            <div class="connection-details">
              <span class="connection-type">{{ link.kind }}</span>
              <span :class="{ 'locked': link.locked }" class="connection-status">
                {{ link.locked ? 'Locked' : 'Open' }}
              </span>
              <span class="connection-id">{{ shortId(link.connection.id) }}</span>
            </div>
          </div>

          <div class="connection-actions">
            <button
                class="row-action view-connection-btn"
                :title="`Open the connection to ${link.targetName}`"
                @click.stop="openConnectionInfo(link.connection)"
            >
              <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.5"/><path d="M8 7.2v3.6M8 5.2v.6"/></svg>
            </button>
            <button
                v-if="editable"
                class="row-action row-action--danger remove-connection-btn"
                :title="`Remove the connection to ${link.targetName}`"
                @click.stop="removeConnection(link.connection)"
            >
              <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><path d="M6.8 9.9a2.4 2.4 0 010-3.4l1.1-1.1M9.2 6.1a2.4 2.4 0 010 3.4l-1.1 1.1M3.5 3.5l9 9"/></svg>
            </button>
          </div>
        </div>
      </div>
      <p v-else class="empty-connections">No connections from this room.</p>
    </div>
  </div>
</template>

<script>
import EditInDjangoAdmin from '@/components/EditInDjangoAdmin.vue';

export default {
  name: 'Connections',
  components: {
    EditInDjangoAdmin
  },
  props: {
    room: {
      type: Object,
      required: true
    },
    roomConnections: {
      type: Array,
      default: () => []
    },
    editorState: {
      type: Object,
      default: null
    },
    editable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    /*
     * WorldEditorConnection carries no direction field, so the old code read
     * `connection.direction` — always undefined — and printed "Unknown" for
     * every horizontal link while defaulting every vertical one to "Down".
     * Direction is derived from the two rooms' grid coordinates instead, using
     * the same mapping the API serialises for players
     * (dx_backend/apps/world/api/serializers/openapi.py:188) so the editor and
     * the game never disagree about which way a door faces.
     */
    resolvedConnections() {
      return this.roomConnections.map(connection => this.describe(connection));
    }
  },
  methods: {
    openConnectionInfo(connection) {
      this.$emit('open-connection-info', connection);
    },

    removeConnection(connection) {
      this.$emit('remove-connection', connection);
    },

    shortId(id) {
      if (id === null || id === undefined) return '';
      const text = String(id);
      return text.length > 10 ? `${text.slice(0, 8)}…` : text;
    },

    roomById(id) {
      return this.editorState?.rooms?.get?.(id) || null;
    },

    describe(connection) {
      const here = this.roomById(this.room.id) || this.room;
      const otherId = connection.fromRoomId === this.room.id
          ? connection.toRoomId
          : connection.fromRoomId;
      const there = this.roomById(otherId);

      const locked = connection.type === 'locked';
      const base = {
        connection,
        locked,
        targetName: there?.getDisplayName?.() || 'Unlinked room',
        isVertical: false,
        up: false,
        bearing: 0,
        label: 'Unknown',
        kind: connection.isVertical ? 'Stairs' : 'Doorway'
      };

      if (!there || !here?.position || !there.position) {
        return base;
      }

      const dx = Math.sign(there.position.grid_x - here.position.grid_x);
      const dy = Math.sign(there.position.grid_y - here.position.grid_y);
      const dz = Math.sign(there.position.grid_z - here.position.grid_z);

      if (dz !== 0) {
        return {
          ...base,
          isVertical: true,
          up: dz > 0,
          label: dz > 0 ? 'Up' : 'Down',
          kind: 'Stairs'
        };
      }

      // grid_y grows southwards, so north is the negative direction. Bearings
      // are clockwise from north, which is exactly how the arrow is rotated.
      const compass = {
        '0,-1': {label: 'North', bearing: 0},
        '1,-1': {label: 'North-East', bearing: 45},
        '1,0': {label: 'East', bearing: 90},
        '1,1': {label: 'South-East', bearing: 135},
        '0,1': {label: 'South', bearing: 180},
        '-1,1': {label: 'South-West', bearing: 225},
        '-1,0': {label: 'West', bearing: 270},
        '-1,-1': {label: 'North-West', bearing: 315}
      }[`${dx},${dy}`];

      if (!compass) {
        // Same cell on the same floor — nothing sensible to point at.
        return {...base, label: 'Same cell', kind: 'Doorway'};
      }

      return {...base, ...compass, kind: 'Doorway'};
    }
  }
};
</script>

<style scoped>
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
}

.connections-container,
.connection-list {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-xs);
}

.connection-item {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  padding: var(--gm-gap-sm);
  cursor: pointer;
  transition: border-color var(--gm-duration) var(--gm-ease);
}

.connection-item:hover {
  border-color: var(--gm-border);
}

/* The bearing dial. A rotated arrow reads as a direction at a glance in a way
   the word never did, and the ladder says "different floor" before you read
   anything. */
.connection-dial {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 26px;
  height: 26px;
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  color: var(--gm-accent);
}

.connection-dial.is-vertical {
  color: var(--gm-info);
}

.dial-glyph {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* Rungs stay narrower and lighter than the arrowhead, so the head remains the
   widest mark and the glyph reads as one arrow rather than two. */
.dial-rungs {
  stroke-width: 1.2;
  opacity: 0.75;
}

.connection-info {
  flex: 1;
  min-width: 0;
}

.connection-header {
  display: flex;
  align-items: baseline;
  gap: var(--gm-gap-xs);
  min-width: 0;
  margin-bottom: 0.1rem;
}

.connection-direction {
  flex: none;
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  font-weight: 600;
  letter-spacing: var(--gm-track-label);
  text-transform: uppercase;
  color: var(--gm-accent);
}

/* Where it goes is the thing a game master is actually looking for. */
.connection-target {
  flex: 1;
  min-width: 0;
  font-size: var(--gm-text-xs);
  color: var(--gm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.connection-details {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  font-size: var(--gm-text-micro);
}

.connection-type {
  color: var(--gm-text-dim);
}

.connection-status {
  color: var(--gm-success);
}

.connection-status.locked {
  color: var(--gm-danger);
}

.connection-id {
  margin-left: auto;
  font-family: var(--gm-font-mono);
  color: var(--gm-text-dim);
  font-variant-numeric: tabular-nums;
}

.connection-actions {
  flex: none;
  display: flex;
  gap: 2px;
}

.empty-connections {
  margin: 0;
  color: var(--gm-text-dim);
  font-size: var(--gm-text-xs);
}

/* Row-level destructive actions stay quiet until reached for — a list of red
   boxes competes with the values it is meant to act on. */
.row-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 22px;
  height: 22px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text-dim);
  cursor: pointer;
  transition: color var(--gm-duration) var(--gm-ease),
              border-color var(--gm-duration) var(--gm-ease),
              background var(--gm-duration) var(--gm-ease);
}

.row-action:hover {
  color: var(--gm-accent);
  border-color: var(--gm-border);
  background: var(--gm-accent-softer);
}

.row-action:focus-visible {
  outline: none;
  border-color: var(--gm-border-hi);
}

.row-action--danger:hover {
  color: var(--gm-danger);
  border-color: var(--gm-danger);
  background: rgba(201, 69, 63, 0.12);
}

.glyph {
  width: 12px;
  height: 12px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

@media (prefers-reduced-motion: reduce) {
  .connection-item,
  .row-action {
    transition: none;
  }
}
</style>
